import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { SendAsEmailModalVariant } from '../../../enums/sendAsEmailModalVariant'
import { SendAsEmailForm } from '../../../types/sendAsEmailForm'
import { getDocumentName } from '../../../utils/getDocumentName'
import { useSendAsEmailModal } from '../contexts/sendAsEmailModalContext'
import { useInvoiceEmailMessage } from './useInvoiceEmailMessage'

export const usePredefinedInvoiceEmailFormValues = (variant: SendAsEmailModalVariant) => {
  const { t } = useTranslation()
  const { organization, isLoading: isLoadingOrganization } = useUserOrganization()
  const { message: predefinedEmailMessage, isLoading: isLoadingPredefinedEmailMessage } =
    useInvoiceEmailMessage(variant)
  const {
    documentLocale,
    documentType,
    invoiceContactPersons,
    invoiceNumber,
    isLoading: isLoadingInvoiceContactPersons,
  } = useSendAsEmailModal()

  const isLoading = isLoadingOrganization || isLoadingPredefinedEmailMessage || isLoadingInvoiceContactPersons

  const paymentReminderEmailSubjectTemplate = t('external_invoices.send_as_email.modal.form.default_reminder')

  const predefinedFormValues: Partial<SendAsEmailForm> = useMemo(() => {
    const predefinedEmailRecipient = invoiceContactPersons.find((contactPerson) => !!contactPerson.isPrimary)?.email
    const predefinedEmailSubject = getDocumentName({
      documentLocale,
      documentType,
      invoiceNumber,
      organizationName: organization?.name,
      paymentReminderEmailSubjectTemplate,
      t,
      variant,
    })

    return {
      ...(predefinedEmailRecipient && { emailRecipients: [predefinedEmailRecipient] }),
      emailSubject: predefinedEmailSubject,
      emailMessage: predefinedEmailMessage,
    }
  }, [
    documentLocale,
    documentType,
    invoiceContactPersons,
    invoiceNumber,
    organization?.name,
    paymentReminderEmailSubjectTemplate,
    predefinedEmailMessage,
    t,
    variant,
  ])

  return { predefinedFormValues, isLoading }
}
